import React, { useEffect, useRef } from 'react'
import {
  BarController, BarElement, CategoryScale, Chart, Legend, LinearScale, Tooltip,
} from 'chart.js'
import { opcionesEjes } from '../utils/graficos'
import { dashboardColors } from '../theme/dashboardColors'

// [RGM] Fase 3 (2026-10-04): tarjeta de la gráfica de BARRAS que TronIA
// adjunta a la respuesta del chat (plan Profesional+). Chart.js directo —
// misma versión (4.5.x) y mismos ejes que HomePage (utils/graficos) — con
// ref + destroy en el cleanup: al reconstruir o desmontar no queda ninguna
// instancia viva. Los ejes usan el gris fijo de colorEjes(): legible en claro
// y oscuro (el canvas no resuelve var()). v1 solo barras (tipo "barras").
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

export interface PuntoGraficaWeb {
  etiqueta: string
  valor: number
}

interface TroniaGraficaProps {
  titulo: string
  datos: PuntoGraficaWeb[]
}

const TroniaGrafica: React.FC<TroniaGraficaProps> = ({ titulo, datos }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || datos.length === 0) return
    // jsdom (tests) no implementa canvas: sin contexto no se crea el chart.
    if (!canvas.getContext('2d')) return

    const ejes = opcionesEjes().scales
    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: datos.map((p) => p.etiqueta),
        datasets: [
          {
            label: titulo,
            data: datos.map((p) => p.valor),
            backgroundColor: dashboardColors.primary,
            borderRadius: 3,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          // El título de la tarjeta ya va arriba; sin leyenda para no robar alto.
          legend: { display: false },
        },
        scales: {
          x: {
            ...ejes.x,
            ticks: { ...ejes.x.ticks, maxRotation: 45, autoSkip: true, font: { size: 10 } },
          },
          y: {
            ...ejes.y,
            ticks: { ...ejes.y.ticks, font: { size: 10 } },
          },
        },
      },
    })

    // Destruye la instancia anterior: sin fugas al reconstruir o desmontar.
    return () => chart.destroy()
  }, [titulo, datos])

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.5rem 0.5rem 0.25rem',
        borderRadius: '0.5rem',
        background: 'var(--cui-body-bg, #fff)',
        border: '1px solid var(--cui-border-color, rgba(0, 0, 0, 0.1))',
      }}
    >
      <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.25rem' }}>{titulo}</div>
      <div style={{ position: 'relative', height: 200, width: '100%' }}>
        <canvas ref={canvasRef} role="img" aria-label={`Gráfica de barras: ${titulo}`} />
      </div>
    </div>
  )
}

export default TroniaGrafica
